/**
 * The setup line for each chapter — what this is, before it starts.
 *
 * It sits directly under the painting, and that placement is what the writing
 * has to earn. The reader has just looked at a picture; the line's job is to
 * turn what they can see into the question the chapter answers. So these point
 * at the image and then past it — the ram in the thicket, the man on the ridge,
 * the gate on the right of the garden — rather than summarising a plot the
 * reader has not met yet.
 *
 * Three rules held throughout. Never give away the takeaway; that is the last
 * screen, not the first. Never more than two sentences, because a setup longer
 * than the first beat of the story is not a setup. And say something true about
 * what is in the painting, since a line that could sit under any picture is
 * decoration rather than orientation.
 *
 * Kept as one file rather than a field on each chapter because they are written
 * as a set. Twenty-eight in a row is the only way to hear that they have drifted
 * into the same sentence shape twenty-eight times, and that is not a thing you
 * can check when they are scattered across seven files.
 */

export const INTROS = {
  // ── In the Beginning ──────────────────────────────────────────────────────
  'ch-creation':
    'Trees, a river, animals at their ease. This is the world working exactly as it was meant to, and it is the last time you will see it that way.',
  'ch-fall':
    'One conversation changes everything. Watch how it starts — not with a demand, but with a question.',
  'ch-flood':
    'Almost everything you remember about this one is from a children’s book. It is not really about the boat; it is about what God promises once the water goes down.',
  'ch-babel':
    'A building project with a stated aim: to make a name for ourselves. It is the last time the Bible speaks about humanity as a whole.',

  // ── A Family ──────────────────────────────────────────────────────────────
  'ch-abram':
    'One man, far from home, promised more descendants than the stars over his tent. He is seventy-five and has no children.',
  'ch-isaac':
    'The hardest story in Genesis, and this app will not soften it. The ram caught in the thicket is the part everyone forgets, and it is the point.',
  'ch-jacob':
    'A cheat, a liar, and God’s chosen man — the same person. Here he spends a night fighting someone he cannot beat.',
  'ch-joseph':
    'Sold by his own brothers to traders heading for Egypt. The question the story keeps asking is who was really behind it.',

  // ── Rescue ────────────────────────────────────────────────────────────────
  'ch-bush':
    'Four hundred years of silence, then a bush that burns without burning up. This is where God tells someone his actual name.',
  'ch-sea':
    'Israel never stopped retelling this one. Notice how little the people being rescued actually do.',
  'ch-sinai':
    'The Ten Commandments, in their proper place — which is after the rescue, not before it. That order changes what the rules are for.',
  'ch-wilderness':
    'An eleven-day journey that took forty years. He is looking at the place they were going, and he is not going in.',

  // ── Kingdom ───────────────────────────────────────────────────────────────
  'ch-joshua':
    'A river that stops, a wall that falls, and one command repeated four times. Courage here means something other than self-belief.',
  'ch-judges':
    'Its bleakest book and one of its kindest stories come from the same years. She is a foreigner, picking up what the harvesters dropped.',
  'ch-david':
    'Seven older brothers were looked at first. The one nobody thought to fetch from the sheep is the one being anointed here.',
  'ch-divided':
    'The kingdom reaches its height and splits in two inside a single generation. This is the moment it goes.',

  // ── Exile ─────────────────────────────────────────────────────────────────
  'ch-prophets':
    'The prophets were not fortune tellers. This one is a farmer, standing at the gate telling the rich exactly what God makes of their religion.',
  'ch-fall-jerusalem':
    'Everything ends: the city, the temple, the kingdom. What people held on to inside the wreckage is the surprising part.',
  'ch-babylon':
    'How do you stay yourself in a place built to absorb you? Three men are about to answer that in public, in front of a furnace.',
  'ch-silence':
    'They came home to rubble and built again, smaller. Then the Old Testament stops mid-sentence and four hundred years pass.',

  // ── The Word ──────────────────────────────────────────────────────────────
  'ch-born':
    'Everything the story has been promising arrives, and almost nobody notices. Pay attention to who does.',
  'ch-kingdom':
    'A hillside, and a description of a kingdom where every usual rule runs backwards. It sounds gentle until you work out what it asks.',
  'ch-cross':
    'Everything so far has been leading here. Three threads running since Genesis meet in a single afternoon.',
  'ch-risen':
    'They came to finish burying him. What they found instead is the claim the entire church stands or falls on.',

  // ── The Church ────────────────────────────────────────────────────────────
  'ch-pentecost':
    'A frightened group in a borrowed room becomes something that will not stop spreading. It begins with wind, fire, and people understanding each other.',
  'ch-paul':
    'The man doing the most damage to the new movement is about to become its most determined missionary. He never quite gets over it.',
  'ch-letters':
    'Most of the New Testament is post. Real letters, to real churches, about real arguments — which is exactly why they still work.',
  'ch-new':
    'Two chapters left, and they answer the first two. See how much of the garden comes back, and what does not.',
};
